/**
 * Subscription & Plans Hooks
 * გამოწერისა და გეგმების hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSubscription,
  getPlans,
  getUsage,
  changePlan,
  cancelSubscription,
  comparePlans,
  canUpgrade,
  upgradePlan,
  cancelUpgrade,
  getInvoices,
  getInvoice,
  getInvoicePayments,
  payInvoice,
  type SubscriptionWithPlan,
  type Plan,
  type UsageStats,
  type PlanComparison,
  type CanUpgradeResponse,
  type ChangePlanRequest,
  type CancelSubscriptionRequest,
  type UpgradePlanRequest,
  type UpgradePlanResponse,
  type Invoice,
  type Payment,
} from '../features/api/subscription';

const QUERY_KEYS = {
  subscription: (userId: string) => ['subscription', userId] as const,
  plans: (userId: string) => ['plans', userId] as const,
  usage: (userId: string) => ['usage', userId] as const,
  comparePlans: ['subscription', 'compare'] as const,
  canUpgrade: (planCode: string) => ['subscription', 'can-upgrade', planCode] as const,
  invoices: (userId: string) => ['invoices', userId] as const,
  invoice: (userId: string, invoiceId: string) => ['invoices', userId, invoiceId] as const,
  invoicePayments: (userId: string, invoiceId: string) => ['invoices', userId, invoiceId, 'payments'] as const,
};

export function useSubscription(userId: string) {
  const { data, isLoading, error, refetch } = useQuery<SubscriptionWithPlan>({
    queryKey: QUERY_KEYS.subscription(userId),
    queryFn: () => getSubscription(userId),
    enabled: !!userId,
  });

  return {
    subscription: data,
    isLoading,
    error,
    refetch,
  };
}

export function usePlans(userId: string) {
  const { data, isLoading, error, refetch } = useQuery<Plan[]>({
    queryKey: QUERY_KEYS.plans(userId),
    queryFn: () => getPlans(userId),
    enabled: !!userId,
  });

  return {
    plans: data,
    isLoading,
    error,
    refetch,
  };
}

export function useUsage(userId: string) {
  const { data, isLoading, error, refetch } = useQuery<UsageStats>({
    queryKey: QUERY_KEYS.usage(userId),
    queryFn: () => getUsage(userId),
    enabled: !!userId,
  });

  return {
    usage: data,
    isLoading,
    error,
    refetch,
  };
}

export function usePlanComparison() {
  const { data, isLoading, error, refetch } = useQuery<PlanComparison>({
    queryKey: QUERY_KEYS.comparePlans,
    queryFn: comparePlans,
  });

  return {
    comparison: data,
    isLoading,
    error,
    refetch,
  };
}

export function useCanUpgrade(planCode: string) {
  const { data, isLoading, error, refetch } = useQuery<CanUpgradeResponse>({
    queryKey: QUERY_KEYS.canUpgrade(planCode),
    queryFn: () => canUpgrade(planCode),
    enabled: !!planCode,
  });

  return {
    canUpgradeResult: data,
    isLoading,
    error,
    refetch,
  };
}

export function useChangePlan(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePlanRequest) => changePlan(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(userId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.usage(userId) });
    },
  });
}

export function useCancelSubscription(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CancelSubscriptionRequest) => cancelSubscription(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(userId) });
    },
  });
}

export function useUpgradePlan() {
  const queryClient = useQueryClient();

  return useMutation<UpgradePlanResponse, Error, UpgradePlanRequest>({
    mutationFn: (data: UpgradePlanRequest) => upgradePlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.comparePlans });
    },
  });
}

export function useCancelUpgrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) => cancelUpgrade(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.comparePlans });
    },
  });
}

export function useInvoices(userId: string) {
  const { data, isLoading, error, refetch } = useQuery<Invoice[]>({
    queryKey: QUERY_KEYS.invoices(userId),
    queryFn: () => getInvoices(userId),
    enabled: !!userId,
  });

  return {
    invoices: data,
    isLoading,
    error,
    refetch,
  };
}

export function useInvoice(userId: string, invoiceId: string) {
  const { data, isLoading, error, refetch } = useQuery<Invoice>({
    queryKey: QUERY_KEYS.invoice(userId, invoiceId),
    queryFn: () => getInvoice(userId, invoiceId),
    enabled: !!userId && !!invoiceId,
  });

  return {
    invoice: data,
    isLoading,
    error,
    refetch,
  };
}

export function useInvoicePayments(userId: string, invoiceId: string) {
  const { data, isLoading, error, refetch } = useQuery<Payment[]>({
    queryKey: QUERY_KEYS.invoicePayments(userId, invoiceId),
    queryFn: () => getInvoicePayments(userId, invoiceId),
    enabled: !!userId && !!invoiceId,
  });

  return {
    payments: data,
    isLoading,
    error,
    refetch,
  };
}

export function usePayInvoice(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) => payInvoice(userId, invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoice(userId, invoiceId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices(userId) });
    },
  });
}
